import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, useScroll, useMotionValueEvent, useMotionValue, useSpring } from 'framer-motion';
import { ScrollContext } from './App';
import { ChevronDown } from 'lucide-react';

import HBM from './assets/HBM.png';
import TOM from './assets/TOM.png';
import CPM from './assets/CPM.png';
import RDM from './assets/RDM.png';
import BBM from './assets/BBM.png';
import HydroBM from './assets/HydroBM.png';
import HDPE from './assets/HDPE.png';
import Bento from './assets/Bento.png';
import TW from './assets/TW.jpg';

const colors = {
  background: '#000',
  textPrimary: '#ffffff',
  textSecondary: '#b0b0b0'
};

const springValues = {
  damping: 30,
  stiffness: 100,
  mass: 2
};

const TiltedCard = ({
  children,
  captionText = '',
  containerHeight = '100%',
  containerWidth = '100%',
  scaleOnHover = 1.05,
  rotateAmplitude = 8,
  showMobileWarning = false,
  showTooltip = true
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useMotionValue(0), springValues);
  const rotateY = useSpring(useMotionValue(0), springValues);
  const scale = useSpring(1, springValues);
  const opacity = useSpring(0);
  const rotateFigcaption = useSpring(0, {
    stiffness: 350,
    damping: 30,
    mass: 1
  });

  const lastY = useRef(0);

  function handleMouse(e) {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;

    const rotationX = (offsetY / (rect.height / 2)) * -rotateAmplitude;
    const rotationY = (offsetX / (rect.width / 2)) * rotateAmplitude;

    rotateX.set(rotationX);
    rotateY.set(rotationY);

    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);

    const velocityY = offsetY - lastY.current;
    rotateFigcaption.set(-velocityY * 0.6);
    lastY.current = offsetY;
  }

  function handleMouseEnter() {
    scale.set(scaleOnHover);
    opacity.set(1);
  }

  function handleMouseLeave() {
    opacity.set(0);
    scale.set(1);
    rotateX.set(0);
    rotateY.set(0);
    rotateFigcaption.set(0);
  }

  return (
    <figure
      ref={ref}
      className="relative w-full h-full [perspective:800px] flex flex-col items-center justify-center"
      style={{
        height: containerHeight,
        width: containerWidth
      }}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showMobileWarning && (
        <div className="absolute top-4 text-center text-sm block sm:hidden">
          This effect is not optimized for mobile. Check on desktop.
        </div>
      )}

      <motion.div
        className="relative [transform-style:preserve-3d] rounded-2xl overflow-hidden"
        style={{
          width: '100%',
          height: '100%',
          rotateX,
          rotateY,
          scale
        }}
      >
        {children}
      </motion.div>

      {showTooltip && (
        <motion.figcaption
          className="pointer-events-none absolute left-0 top-0 rounded-[4px] bg-white px-[10px] py-[4px] text-[10px] text-[#2d2d2d] opacity-0 z-[3] hidden sm:block"
          style={{
            x,
            y,
            opacity,
            rotate: rotateFigcaption
          }}
        >
          {captionText}
        </motion.figcaption>
      )}
    </figure>
  );
};

const ScrollIndicator = () => {
  const { scrollYProgress } = useScroll();
  const [isVisible, setIsVisible] = useState(true);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setIsVisible(latest < 0.05);
  });

  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight * 0.9,
      behavior: 'smooth'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.5 }}
      className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 cursor-pointer"
      onClick={handleScrollDown}
    >
      <motion.div 
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center"
      >
        <ChevronDown 
          size={48} 
          color="#ffffff"
          opacity={0.2} 
        />
      </motion.div>
    </motion.div>
  );
};

const Products = () => {
  const { setScrollContainer } = useContext(ScrollContext);
  const scrollRef = useRef(null);

  useEffect(() => {
    setScrollContainer(scrollRef.current);
    return () => setScrollContainer(null);
  }, [setScrollContainer]);

  const products = [
    {
      title: 'Tape Wrapping Machine',
      description: 'Advanced wrapping machine for comprehensive protection of ductile iron and carbon steel pipes.',
      imageSrc: TW,
    },
    {
      title: 'Transformer Oil Filtration',
      description: 'State-of-the-art filtration system for maintaining optimal transformer oil purity and performance.',
      imageSrc: TOM,
    },
    {
      title: 'Rock Drilling Machine',
      description: 'Powerful and robust drilling equipment designed for the toughest mining and construction projects.',
      imageSrc: RDM,
    },
    {
      title: 'Cable Pulling Winch',
      description: 'Heavy-duty winch system engineered for smooth and efficient underground cable installation.',
      imageSrc: CPM,
    },
    {
      title: 'Bar Bending Machine',
      description: 'Precision bending solutions for construction reinforcement bars, ensuring structural integrity.',
      imageSrc: BBM,
    },
    {
      title: 'HDPE Pipes',
      description: 'Durable, high-quality HDPE pipes providing secure and long-lasting infrastructure for cable laying.',
      imageSrc: HDPE,
    },
    {
      title: 'Hydraulic Bar Bending',
      description: 'Hydraulic-powered bar bending machine for effortless and accurate shaping of reinforcement bars.',
      imageSrc: HydroBM,
    },
    {
      title: 'Hydro Blasting Machine',
      description: 'High-pressure water jetting for industrial cleaning applications with advanced control systems.',
      imageSrc: HBM, 
    },
    {
      title: 'Bentonite',
      description: 'Premium-grade, fine bentonite crucial for effective and stable horizontal directional drilling operations.',
      imageSrc: Bento,
    },
  ];

  return (
    <div ref={scrollRef} className="h-screen overflow-y-auto overflow-x-hidden custom-scrollbar">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className=""
      >
        <div style={{ color: colors.textPrimary }} className="relative pt-32 pb-16">
          <ScrollIndicator />

          <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold mb-6 text-white">
              Our Products 
            </h1>
            <p style={{ color: colors.textSecondary }} className="text-lg sm:text-xl max-w-3xl mx-auto">
              Innovative industrial solutions, built for performance, reliability, and excellence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <TiltedCard captionText={product.title}>
                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden h-full flex flex-col">
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundColor: colors.background }}
                    />
                    <div className="relative aspect-video overflow-hidden">
                      <img 
                        src={product.imageSrc} 
                        alt={product.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="relative p-6 flex-grow flex flex-col">
                      <h3 className="text-2xl font-bold text-white mb-2 font-heading">
                        {product.title}
                      </h3>
                      <p className="text-neutral-400 text-sm flex-grow">
                        {product.description}
                      </p>
                    </div>
                  </div>
                </TiltedCard>
              </motion.div>
            ))}
          </div>
          </div>
        </div>
              <footer className="w-full py-4">
        <p className="text-center text-sm text-neutral-600 dark:text-neutral-500">
            © 2025 Chavi Group . All rights reserved.
        </p>
    </footer>
      </motion.div>
    </div>
  );
};

export default Products;